/* eslint-disable react-hooks/exhaustive-deps */
import { useLocation, useParams } from "react-router-dom";
import "./contractTemplatePage.scss";
import Loading from "../../components/common/Loading";
import { ClauseEntity, ContractEntity, ContractTemplateEntity, SubClauseEntity, InitialContractTemplateEntity, GroupEntity } from "../../domain/entities";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import useApiClientWithLoading from "../../services/api/ApiClient";
import { LoadingContext } from "../../contexts/LoadingContext";
import EditContractTemplate from "./components/EditContractTemplate";
import EditContractTemplateContext, { EditContractTemplateContextType } from "../../contexts/EditContractTemplateContext";
import { EnumOption, ListOption, TableLigne, SegmentedClauseParam, SegmentedTextType, SegmentedEnumParam, ListParam, TableParam, SegmentedBooleanParam, SegmentedText, SegmentedBeneficialParam, SegmentedBeneficialListParam, RenderSegments } from "../../domain/types/ClauseParams";
import { updateSegment, insertParamInSegment, genIdFactory, cleanSegmentation, deleteSegment, fixTemplateIndexation, idfySegmentation, styleSegmentation } from "../../domain/ContractTemplate";
import { ContractTemplateClient } from "../../services/api/ContractTemplateClient";
import { InitialContractTemplateClient } from "../../services/api/InitialContractTemplateClient"
import { useTranslation } from "../../contexts/TranslationProvider";
import EventManager from "../../services/EventManager";
import { toast } from "react-toastify";
import { normalizeName } from "../../utils/string";
import { GroupClient } from "../../services/api/GroupClient";
import { removeDuplicates } from "../../utils/array";


function ContractTemplatePage() {
	const location = useLocation();
	const { isLoading, setLoading } = useContext(LoadingContext);
	const { setLanguage, language, t } = useTranslation();
	const translationPath = "pages.contractTemplateParam."


	const [contractTemplate, setContractTemplate] = useState<ContractTemplateEntity>(null);
	const [initialTemplate, setInitialTemplate] = useState<InitialContractTemplateEntity>(null);
	const [combinedTemplateParams, setCombinedTemplateParams] = useState<SegmentedClauseParam[]>([]);
	const [groups, setGroups] = useState<GroupEntity[]>([]);
	const [previewValues, setPreviewValues] = useState<ContractEntity['paramValues']>({});

	const { contractTemplateId } = useParams<{ contractTemplateId?: string }>() || {};
	const apiClient = useApiClientWithLoading(setLoading);
	const contractTemplateClient = new ContractTemplateClient(apiClient)
	const initialContractTemplateClient = new InitialContractTemplateClient(apiClient)
	const groupClient = new GroupClient(apiClient)
	const setTemplateGroups = (group: GroupEntity, add: boolean) => {
		let newGroups = [...contractTemplate.groups];
		if (add) {
			newGroups = [...contractTemplate.groups, {
				...group,
				Group_ContractTemplate: {
					clauseCodes: contractTemplate.clauses.map(clause => clause.index),
					viewClauseCodes: []
				}
			}];
		} else {
			newGroups = newGroups.filter(g => g.id !== group.id);
		}
		const newContractTemplate = { ...contractTemplate };
		newContractTemplate.groups = newGroups;
		setContractTemplate(newContractTemplate);
	}
	const onSelectGroupInClause = async (index: string, newGroups: GroupEntity[], newViewGroups: GroupEntity[] = []) => {
		const templateGroups = contractTemplate.groups;
		templateGroups.forEach(group => {
			if (newGroups.some(g => g.id === group.id)) {
				if (!group.Group_ContractTemplate.clauseCodes.includes(index)) {
					group.Group_ContractTemplate.clauseCodes.push(index);
				}
			} else {
				group.Group_ContractTemplate.clauseCodes = group.Group_ContractTemplate.clauseCodes.filter(code => code !== index);
			}
		})
		templateGroups.forEach(group => {
			if (newViewGroups.some(g => g.id === group.id)) {
				if (!group.Group_ContractTemplate.viewClauseCodes.includes(index)) {
					group.Group_ContractTemplate.viewClauseCodes.push(index);
				}
			} else {
				group.Group_ContractTemplate.viewClauseCodes = group.Group_ContractTemplate.viewClauseCodes.filter(code => code !== index);
			}
		})
		const updatedContractTemplate = { ...contractTemplate };
		updatedContractTemplate.groups = templateGroups;
		setContractTemplate(updatedContractTemplate);
	};

	const prepareTemplate = (template: ContractTemplateEntity) => {
		const clauses = template.clauses?.map(clause => {
			const newClause = { ...clause }
			newClause.segmentation.segmentedParams = newClause.segmentation.segmentedParams ?? []
			if (newClause.segmentation.segmentedText.length == 0) {
				newClause.segmentation.segmentedText = [["", "_", SegmentedTextType.STATIC]]
			}
			newClause.segmentation = idfySegmentation(newClause.segmentation, genIdFactory(clause.index))
			newClause.subClauses = clause.subClauses?.map(subClause => {
				const newSubClause = { ...subClause }
				newSubClause.segmentation.segmentedParams = newSubClause.segmentation.segmentedParams ?? []
				if (newSubClause.segmentation.segmentedText.length == 0) {
					newSubClause.segmentation.segmentedText = [["", "_", SegmentedTextType.STATIC]]
				}
				newSubClause.segmentation = idfySegmentation(newSubClause.segmentation, genIdFactory(subClause.index))
				return newSubClause
			})
			return newClause
		})
		return { ...template, clauses }
	}
	useEffect(() => {
		(async () => {
			const row = await contractTemplateClient.getById(parseInt(contractTemplateId));
			if (!row) return;
			if (row.languages) {
				const { languages } = row;
				if (languages?.length !== 0 && (!(languages as string[]).includes(language))) {
					setLanguage(languages[0]);
				}
			}
			const groupData = await groupClient.getAll();
			setGroups(groupData.rows);
			setContractTemplate(prepareTemplate(row));
		})();
	}, []);

	useEffect(() => {
		(async () => {
			try {
				const result = await initialContractTemplateClient.getOriginal(parseInt(contractTemplateId));
				if (!result) return;
				setInitialTemplate((result as any).row);
			} catch (e) {
				console.error(e)
			}
		})();
	}, []);

	useEffect(() => {
		if (!contractTemplate) return;
		// combine params in contractTemplate
		const combinedParams = contractTemplate.clauses.reduce((acc, clause) => {
			const clauseParams = clause.segmentation.segmentedParams
			acc.push(...clauseParams);

			clause.subClauses?.forEach(subClause => {
				const subClauseParams = subClause.segmentation.segmentedParams
				acc.push(...subClauseParams);
			});

			return acc;
		}, [] as SegmentedClauseParam[]);

		// Filter out duplicate parameters based on the 'name' property
		const uniqueParams = combinedParams.reduce((acc, param) => {
			if (!acc.some(p => p.name === param.name)) {
				acc.push(param);
			}
			return acc;
		}, [] as SegmentedClauseParam[]);

		setCombinedTemplateParams(uniqueParams);

	}, [contractTemplate]);

	const updateInitialTemplate = async () => {
		try {
			const result = await initialContractTemplateClient.getOriginal(parseInt(contractTemplateId));
			if (!result) return;
			setInitialTemplate((result as any).row);
		} catch (e) {
			console.error(e)
		}
	}

	const insertClauseInContractTemplate = async (name) => {
		const result = await contractTemplateClient.createClause(parseInt(contractTemplateId), name);
		if (!result.row) return;
		return result.row
	}

	const insertExistingClauseInContractTemplate = async (clauseId: ClauseEntity['id'], newName: string) => {
		const result = await contractTemplateClient.addExistingClause(parseInt(contractTemplateId), clauseId, newName);
		if (!result.row) return;
		return result.row
	}

	const insertSubClauseInContractTemplate = async (clauseId: ClauseEntity['id'], name: string) => {
		const result = await contractTemplateClient.createSubClause(clauseId, name);
		if (!result.row) return;
		return result.row
	}

	const fixClauseIndexation = (clause: ClauseEntity) => {
		const newSubClauses = clause.subClauses?.map((subClause, index) => {
			subClause.index = (index + 1).toString()

			subClause.segmentation = idfySegmentation(subClause.segmentation, genIdFactory(subClause.index))
			return subClause
		})
		return { ...clause, subClauses: newSubClauses }
	}

	const findSegmentationExistingParamName = (segmentation: ClauseEntity['segmentation'], name: string) => {
		const existingParam = segmentation.segmentedParams.find(p => p.name === name);
		if (existingParam) {
			return existingParam
		}
		return null
	}
	const findExistingParamName = (name: string) => {
		contractTemplate.clauses.forEach(clause => {
			const existingParam = findSegmentationExistingParamName(clause.segmentation, name)
			if (existingParam)
				return existingParam
			clause.subClauses.forEach(subClause => {
				const existingParam = findSegmentationExistingParamName(subClause.segmentation, name)
				if (existingParam)
					return existingParam
			})
		})
		return null

	}
	const isParamSegment = (segment: SegmentedText[number], param: SegmentedClauseParam) => {
		const [id, value, type] = segment

		return type == SegmentedTextType.PARAM && (value == param.name || value.startsWith(`${param.name}.`))
	}
	const onSegmentationParamNameChanged = (segmentation: ClauseEntity['segmentation'], param: SegmentedClauseParam, newValue: string) => {
		segmentation.segmentedText = segmentation.segmentedText.map((segment) => {
			const [id, value, type] = segment
			if (isParamSegment(segment, param)) {
				return [id, value.replace(param.name, newValue), type]
			}
			return segment
		})

		segmentation.segmentedParams = segmentation.segmentedParams.map((segParam) => {
			switch (segParam.type) {
				case 'enum':
					return {
						...segParam,
						name: segParam.name == param.name ? newValue : segParam.name,
						args: segParam.args.map((option) => {
							return {
								...option,
								text: onSegmentationParamNameChanged({ segmentedText: option.text, segmentedParams: [] }, param, newValue).segmentedText
							}
						})
					}
				case 'boolean':
					return {
						...segParam,
						name: segParam.name == param.name ? newValue : segParam.name,
						args: {
							textIfTrue: onSegmentationParamNameChanged({ segmentedText: segParam.args.textIfTrue, segmentedParams: [] }, param, newValue).segmentedText,
							textIfFalse: onSegmentationParamNameChanged({ segmentedText: segParam.args.textIfFalse, segmentedParams: [] }, param, newValue).segmentedText
						}
					}
				case 'static-table':
					return {
						...segParam,
						name: segParam.name == param.name ? newValue : segParam.name,
						args: {
							...segParam.args,
							cells: segParam.args?.cells.map((row) => {
								return row.map((cell) => {
									return {
										...cell,
										content: onSegmentationParamNameChanged({ segmentedText: cell.content, segmentedParams: [] }, param, newValue).segmentedText,
									}
								})
							})
						}
					}
				default:
					return {
						...segParam,
						name: segParam.name == param.name ? newValue : segParam.name
					}
			}
		})
		return segmentation
	}
	const onParamNameChanged = (
		param: SegmentedClauseParam,
		newValue: string
	) => {
		const existingParam = findExistingParamName(newValue)
		if (existingParam) {
			console.log("oups");
			return
		}
		let newContractTemplateValue = { ...contractTemplate }
		// update all params with the same name and type
		newContractTemplateValue.clauses.forEach(clause => {
			clause.segmentation = onSegmentationParamNameChanged(clause.segmentation, param, newValue)
			clause.subClauses.forEach(subClause => {
				subClause.segmentation = onSegmentationParamNameChanged(subClause.segmentation, param, newValue)
				return subClause
			})
		})

		setContractTemplate(newContractTemplateValue)
	}
	const onSegmentaionParamChanged = (segmentation: ClauseEntity['segmentation'], param: SegmentedClauseParam) => {
		switch (param.type) {
			case 'beneficial':
			case 'beneficial[]':
			case 'comment':
			case 'csv':
			case 'date':
			case 'list':
			case 'table':
			case 'property':
			case 'number':
				segmentation.segmentedParams = segmentation.segmentedParams.map((segParam) => {
					if (segParam.name == param.name) {
						return {
							...param,
						}
					}
					return segParam
				})
				break
			case 'enum':
			case 'boolean':
				segmentation.segmentedParams = segmentation.segmentedParams.map((segParam) => {
					if (segParam.name == param.name) {
						return {
							...segParam,
							label: param.label,
						}
					}
					return segParam
				})
				break;

			default:
				break;
		}
		return segmentation
	}
	const onParamChanged: EditContractTemplateContextType['onParamChanged'] = (clauseId: ClauseEntity['id'], subClauseId: SubClauseEntity['id'], param: SegmentedClauseParam) => {
		let clause: ClauseEntity
		let subClause: SubClauseEntity
		let clauseIdx: number
		let subClauseIdx: number
		let segmentation: ClauseEntity['segmentation']

		clauseIdx = contractTemplate.clauses!.findIndex(c => c.id == clauseId)
		clause = contractTemplate.clauses[clauseIdx]

		if (subClauseId) {
			subClauseIdx = clause.subClauses!.findIndex(sc => sc.id == subClauseId)
			subClause = clause.subClauses[subClauseIdx]
			segmentation = subClause.segmentation
		} else {
			segmentation = clause.segmentation
		}
		switch (param.type) {
			case 'enum':
			case 'boolean':
				segmentation.segmentedParams = segmentation.segmentedParams.map((segParam) => {
					if (segParam.name == param.name && param.definition == (segParam as any).definition) {
						return {
							...param,
						}
					}
					return segParam
				})
				break;
			default:
				segmentation.segmentedParams = segmentation.segmentedParams.map((segParam) => {
					if (segParam.name == param.name) {
						return {
							...param,
						}
					}
					return segParam
				}
				)
				break;
		}

		if (subClauseId) {
			segmentation = idfySegmentation(segmentation, genIdFactory(subClause.index))
			subClause.segmentation = segmentation
			clause.subClauses[subClauseIdx] = subClause
			clause.subClauses = [...clause.subClauses]
		} else {
			segmentation = idfySegmentation(segmentation, genIdFactory(clause.index))
			clause.segmentation = segmentation
		}
		let newContractTemplateValue = { ...contractTemplate }
		// update all params with the same name and type
		newContractTemplateValue.clauses.forEach(clause => {
			clause.segmentation = onSegmentaionParamChanged(clause.segmentation, param)
			clause.subClauses.forEach(subClause => {
				subClause.segmentation = onSegmentaionParamChanged(subClause.segmentation, param)
				return subClause
			})
		})

		newContractTemplateValue.clauses[clauseIdx] = clause
		setContractTemplate(newContractTemplateValue)
	}

	const onApplySegmentation: EditContractTemplateContextType['onApplySegmentation'] = async (clauseId: ClauseEntity['id'], subClauseId: SubClauseEntity['id'], styledSegments: ClauseEntity['segmentation']['segmentedText'], deletedSegments: RenderSegments) => {
		let clause: ClauseEntity
		let subClause: SubClauseEntity
		let clauseIdx: number
		let subClauseIdx: number
		let segmentation: ClauseEntity['segmentation']
		clauseIdx = contractTemplate.clauses!.findIndex(c => c.id == clauseId)
		clause = contractTemplate.clauses[clauseIdx]

		if (subClauseId) {
			subClauseIdx = clause.subClauses!.findIndex(sc => sc.id == subClauseId)
			subClause = clause.subClauses[subClauseIdx]
			segmentation = subClause.segmentation
		} else {
			segmentation = clause.segmentation
		}
		const styledSegmentation = styleSegmentation(segmentation, styledSegments, deletedSegments)
		const newSegmentation = idfySegmentation(styledSegmentation, genIdFactory(subClauseId ? subClause.index : clause.index))
		if (subClauseId) {
			subClause.segmentation = newSegmentation
			clause.subClauses[subClauseIdx] = subClause
			clause.subClauses = [...clause.subClauses]
		} else {
			clause.segmentation = newSegmentation
		}
		const newContractTemplateValue = { ...contractTemplate }
		newContractTemplateValue.clauses[clauseIdx] = clause
		setContractTemplate(newContractTemplateValue)
	}

	const onParamDelete: EditContractTemplateContextType['onParamDelete'] = (clauseId: ClauseEntity['id'], subClauseId: SubClauseEntity['id'], param: SegmentedClauseParam) => {
		let clause: ClauseEntity
		let subClause: SubClauseEntity
		let clauseIdx: number
		let subClauseIdx: number
		let segmentation: ClauseEntity['segmentation']
		clauseIdx = contractTemplate.clauses!.findIndex(c => c.id == clauseId)
		clause = contractTemplate.clauses[clauseIdx]

		if (subClauseId) {
			subClauseIdx = clause.subClauses!.findIndex(sc => sc.id == subClauseId)
			subClause = clause.subClauses[subClauseIdx]
			segmentation = subClause.segmentation
		} else {
			segmentation = clause.segmentation
		}
		segmentation.segmentedParams = segmentation.segmentedParams.filter(segP => segP.name !== param.name)
		segmentation.segmentedText = segmentation.segmentedText.map(segment => {
			if (isParamSegment(segment, param))
				return [segment[0], segment[1], SegmentedTextType.STATIC]
			return segment
		})
		if (subClauseId) {
			segmentation = idfySegmentation(segmentation, genIdFactory(subClause.index))
			subClause.segmentation = segmentation
			clause.subClauses = [...clause.subClauses]
		} else {
			segmentation = idfySegmentation(segmentation, genIdFactory(clause.index))
			clause.segmentation = segmentation
		}
		let newContractTemplateValue = { ...contractTemplate }
		setContractTemplate(newContractTemplateValue);
	}
	const onSegmentaionParamTypeChanged = (segmentation: ClauseEntity['segmentation'], param: SegmentedClauseParam, type: SegmentedClauseParam['type']) => {
		let definition = 0
		segmentation.segmentedParams = segmentation.segmentedParams.map((segP) => {
			if (segP.name == param.name) {
				switch (type) {
					case "boolean":
						return {
							name: param.name,
							label: param.label,
							type: 'boolean',
							definition: definition++,
							args: {
								textIfTrue: [["", "_", SegmentedTextType.STATIC]],
								textIfFalse: [["", "_", SegmentedTextType.STATIC]],
							}
						} as SegmentedClauseParam
					case "enum":
						return {
							name: param.name,
							label: param.label,
							type: 'enum',
							definition: definition++,
							args: []
						} as SegmentedClauseParam
					case "beneficial":
						return {
							name: param.name,
							label: param.label,
							type: 'beneficial',
							args: {
								beneficialTypes: []
							}
						} as SegmentedClauseParam
					case "beneficial[]":
						return {
							name: param.name,
							label: param.label,
							type: 'beneficial[]',
							args: {
								beneficialTypes: ['Company', 'Minor', 'Person', 'Joint', 'Attorney'],
							}
						} as SegmentedClauseParam
					case "list":
					case "table":
						return {
							name: param.name,
							label: param.label,
							type,
							args: []
						} as SegmentedClauseParam
					case "property":
						return {
							name: param.name,
							label: param.label,
							type,
							args: { tag: "" }
						} as SegmentedClauseParam
					case "number":
					case "date":
					case "comment":
					case "csv":
					case "file":
					case "string":
						return {
							name: param.name,
							label: param.label,
							type,
						} as SegmentedClauseParam
					default:
						return segP
				}
			}
			return segP
		})

		return segmentation
	}
	const onParamTypeChange: EditContractTemplateContextType['onParamTypeChange'] = (param: SegmentedClauseParam, type: SegmentedClauseParam['type']) => {
		let newContractTemplateValue = { ...contractTemplate }
		newContractTemplateValue.clauses.forEach(clause => {
			clause.segmentation = onSegmentaionParamTypeChanged(clause.segmentation, param, type)
			clause.segmentation = idfySegmentation(clause.segmentation, genIdFactory(clause.index))
			clause.subClauses.map(subClause => {
				subClause.segmentation = onSegmentaionParamTypeChanged(subClause.segmentation, param, type)
				subClause.segmentation = idfySegmentation(subClause.segmentation, genIdFactory(subClause.index))
				return subClause
			})
		})
		setContractTemplate(newContractTemplateValue)
	}
	const onSegmentationOptionDelete = (segmentation: ClauseEntity['segmentation'], param: SegmentedClauseParam, index: number) => {
		segmentation.segmentedParams = segmentation.segmentedParams.map((segP) => {
			if (segP.name == param.name) {
				switch (param.type) {
					case "enum":
					case "list":
					case "table":
						return {
							...param,
							args: param.args.filter((arg, idx) => idx != index)
						} as SegmentedClauseParam
					default:
						return segP
				}
			}
			return segP
		})

		return segmentation
	}
	const onOptionDelete: EditContractTemplateContextType['onOptionDelete'] = (param: SegmentedClauseParam, index: number) => {
		let newContractTemplateValue = { ...contractTemplate }
		newContractTemplateValue.clauses.forEach(clause => {
			clause.segmentation = onSegmentationOptionDelete(clause.segmentation, param, index)
			clause.segmentation = idfySegmentation(clause.segmentation, genIdFactory(clause.index))
			clause.subClauses.map(subClause => {
				subClause.segmentation = onSegmentationOptionDelete(subClause.segmentation, param, index)
				subClause.segmentation = idfySegmentation(subClause.segmentation, genIdFactory(subClause.index))
				return subClause
			})
		})
		setContractTemplate(newContractTemplateValue)
	}

	const onSegmentationOptionChanged = (segmentation: ClauseEntity['segmentation'], param: SegmentedEnumParam | ListParam,
		option: SegmentedEnumParam['args'][number]['option'] | ListParam['args'][number], index: number) => {
		segmentation.segmentedParams = segmentation.segmentedParams.map((segP) => {
			if (segP.name == param.name && segP.type == param.type) {
				switch (segP.type) {
					case "enum":
						return {
							...segP,
							args: segP.args.map((arg, idx) => idx == index ? {
								text: arg.text,
								option: option
							} : arg)
						} as SegmentedEnumParam
					case "list":
						return {
							...segP,
							args: segP.args.map((arg, idx) => idx == index ? option : arg)
						} as ListParam
					default:
						return segP
				}
			}
			return segP
		})

		return segmentation
	}
	const onOptionChanged: EditContractTemplateContextType['onOptionChanged'] = (param: SegmentedEnumParam | ListParam,
		option: SegmentedEnumParam['args'][number]['option'] | ListParam['args'][number], index: number) => {
		let newContractTemplateValue = { ...contractTemplate }
		newContractTemplateValue.clauses.forEach(clause => {
			clause.segmentation = onSegmentationOptionChanged(clause.segmentation, param, option, index)
			clause.segmentation = idfySegmentation(clause.segmentation, genIdFactory(clause.index))
			clause.subClauses.map(subClause => {
				subClause.segmentation = onSegmentationOptionChanged(subClause.segmentation, param, option, index)
				subClause.segmentation = idfySegmentation(subClause.segmentation, genIdFactory(subClause.index))
				return subClause
			})
		})
		setContractTemplate(newContractTemplateValue)
	}
	const onSegmentationOptionAdd = (segmentation: ClauseEntity['segmentation'],
		param: SegmentedEnumParam | ListParam | TableParam | SegmentedBooleanParam,
		option: EnumOption | ListOption | TableLigne | boolean) => {
		segmentation.segmentedParams = segmentation.segmentedParams.map((segP) => {
			if (segP.name == param.name && segP.type == param.type) {
				switch (segP.type) {
					case "enum":
						segP.args.push({
							text: [["", "_", SegmentedTextType.STATIC]],
							option: (option as EnumOption).option
						})
						return segP
					case "list":
						segP.args.push(option as ListOption)
						return segP
					case "table":
						segP.args.push(option as TableLigne)
						return segP
					case "boolean":
						if (option) {
							segP.args.textIfTrue = [["", "_", SegmentedTextType.STATIC]]
						} else {
							segP.args.textIfFalse = [["", "_", SegmentedTextType.STATIC]]
						}
						return segP
					default:
						return segP
				}
			}
			return segP
		})
		return segmentation
	}
	const onOptionAdd: EditContractTemplateContextType['onOptionAdd'] = (
		param: SegmentedEnumParam | ListParam | TableParam | SegmentedBooleanParam,
		option: EnumOption | ListOption | TableLigne | boolean
	) => {
		let newContractTemplateValue = { ...contractTemplate }
		newContractTemplateValue.clauses.forEach(clause => {
			clause.segmentation = onSegmentationOptionAdd(clause.segmentation, param, option)
			clause.segmentation = idfySegmentation(clause.segmentation, genIdFactory(clause.index))
			clause.subClauses.map(subClause => {
				subClause.segmentation = onSegmentationOptionAdd(subClause.segmentation, param, option)
				subClause.segmentation = idfySegmentation(subClause.segmentation, genIdFactory(subClause.index))
				return subClause
			})
		})
		setContractTemplate(newContractTemplateValue)
	};

	const onSegmentChange: EditContractTemplateContextType['onSegmentChange'] = (clauseId: ClauseEntity['id'], subClauseId: ClauseEntity['id'], id: string, segmentText: string) => {
		let clause: ClauseEntity
		let subClause: SubClauseEntity
		let clauseIdx: number
		let subClauseIdx: number
		let segmentation: ClauseEntity['segmentation']
		clauseIdx = contractTemplate.clauses!.findIndex(c => c.id == clauseId)
		clause = contractTemplate.clauses[clauseIdx]

		if (subClauseId) {
			subClauseIdx = clause.subClauses!.findIndex(sc => sc.id == subClauseId)
			subClause = clause.subClauses[subClauseIdx]
			segmentation = subClause.segmentation
		} else {
			segmentation = clause.segmentation
		}
		segmentation = updateSegment(segmentation.segmentedText, segmentation.segmentedParams, id, segmentText)
		segmentation = idfySegmentation(segmentation, genIdFactory(subClause?.index || clause.index))

		if (subClauseId) {
			segmentation = idfySegmentation(segmentation, genIdFactory(subClause.index))
			subClause.segmentation = segmentation
			clause.subClauses = [...clause.subClauses]
		} else {
			segmentation = idfySegmentation(segmentation, genIdFactory(clause.index))
			clause.segmentation = segmentation
		}
		let newContractTemplateValue = { ...contractTemplate }
		newContractTemplateValue.clauses[clauseIdx] = clause
		setContractTemplate(newContractTemplateValue)

	}

	const onAddParam: EditContractTemplateContextType['onAddParam'] = (clauseId: ClauseEntity['id'], subClauseId: ClauseEntity['id'], id: string, newParam: SegmentedClauseParam, textBefore: string, textAfter: string, segments: ClauseEntity['segmentation']['segmentedText'], field: string, deletedSegments: RenderSegments) => {
		let clause: ClauseEntity
		let subClause: SubClauseEntity
		let segmentation: ClauseEntity['segmentation']
		let clauseIdx: number
		let subClauseIdx: number

		clauseIdx = contractTemplate.clauses!.findIndex(c => c.id == clauseId)
		clause = contractTemplate.clauses[clauseIdx]

		if ((newParam.type == "beneficial" || newParam.type == "beneficial[]")) {
			if (combinedTemplateParams.find(p => p.name === newParam.name && p.type === newParam.type)) {
				newParam.args = (combinedTemplateParams.find(p => p.name === newParam.name && p.type === newParam.type) as any).args
			}
		}
		
		if ((newParam.type == "number")) {
		newParam.format=`formatWithSpaces(${newParam.name})`
		}

		if (subClauseId) {
			subClauseIdx = clause.subClauses!.findIndex(sc => sc.id == subClauseId)
			subClause = clause.subClauses[subClauseIdx]
			segmentation = subClause.segmentation
		} else {
			segmentation = clause.segmentation
		}
		const existingParam = segmentation.segmentedParams.find(p => p.name === newParam.name && p.type === newParam.type)
		let newSegmentation
		if (!existingParam) {
			newSegmentation = insertParamInSegment(segmentation.segmentedText, segmentation.segmentedParams, id, newParam, 0, textBefore, textAfter, field, segments, deletedSegments)
		} else {
			const definitions = segmentation.segmentedParams.filter((param) => param.name == newParam.name && param.type == newParam.type)
			const definition = definitions.reduce((prev, curr) => Math.max(prev, (curr as any).definition || 0), 0) + 1
			newSegmentation = insertParamInSegment(segmentation.segmentedText, segmentation.segmentedParams, id, newParam, definition, textBefore, textAfter, field, segments, deletedSegments)
		}
		if (subClauseId) {
			segmentation = idfySegmentation(newSegmentation, genIdFactory(subClause.index))
			subClause.segmentation = segmentation
			clause.subClauses = [...clause.subClauses]
		} else {
			segmentation = idfySegmentation(newSegmentation, genIdFactory(clause.index))
			clause.segmentation = segmentation
		}

		let newContractTemplateValue = { ...contractTemplate }
		newContractTemplateValue.clauses[clauseIdx] = clause
		setContractTemplate(newContractTemplateValue)
	}

	const onParamReorder: EditContractTemplateContextType['onParamReorder'] = (clauseId: ClauseEntity['id'], subClauseId: ClauseEntity['id'], oldIndex: number, newIndex: number) => {
		let clause: ClauseEntity
		let subClause: SubClauseEntity
		let clauseIdx: number
		let subClauseIdx: number
		let segmentation: ClauseEntity['segmentation']
		clauseIdx = contractTemplate.clauses!.findIndex(c => c.id == clauseId)
		clause = contractTemplate.clauses[clauseIdx]

		if (subClauseId) {
			subClauseIdx = clause.subClauses!.findIndex(sc => sc.id == subClauseId)
			subClause = clause.subClauses[subClauseIdx]
			segmentation = subClause.segmentation
		} else {
			segmentation = clause.segmentation
		}
		const uniqueParams = removeDuplicates(segmentation.segmentedParams, "name")
		const duplicateParams = segmentation.segmentedParams.filter((param, index) => {
			return uniqueParams.findIndex(p => p.name === param.name) !== index
		})
		const [removed] = uniqueParams.splice(oldIndex, 1)
		uniqueParams.splice(newIndex, 0, removed)
		const newParams = uniqueParams.concat(duplicateParams)
		segmentation.segmentedParams = newParams
		if (subClauseId) {
			segmentation = idfySegmentation(segmentation, genIdFactory(subClause.index))
			subClause.segmentation = segmentation
			clause.subClauses = [...clause.subClauses]
		} else {
			segmentation = idfySegmentation(segmentation, genIdFactory(clause.index))
			clause.segmentation = segmentation
		}
		let newContractTemplateValue = { ...contractTemplate }
		newContractTemplateValue.clauses[clauseIdx] = clause
		setContractTemplate(newContractTemplateValue)
	}

	const onClauseDelete: EditContractTemplateContextType['onClauseDelete'] = async (clauseId: ClauseEntity['id'], subClauseId: SubClauseEntity['id'], applyMerge: Boolean): Promise<ContractTemplateEntity> => {
		let clause: ClauseEntity
		let subClause: SubClauseEntity
		let clauseIdx: number
		let subClauseIdx: number
		clauseIdx = contractTemplate.clauses!.findIndex(c => c.id == clauseId)
		clause = contractTemplate.clauses[clauseIdx]

		if (subClauseId) {
			subClauseIdx = clause.subClauses!.findIndex(sc => sc.id == subClauseId)
			subClause = clause.subClauses[subClauseIdx]
		}

		let newContractTemplateValue = { ...contractTemplate }
		clause = { ...clause }
		if (subClause) {
			if (applyMerge) {
				if (subClauseIdx === 0) {
					clause.segmentation.segmentedText = [
						...clause.segmentation.segmentedText,
						["", `\n${subClause.name}\n`, SegmentedTextType.STATIC],
						...subClause.segmentation.segmentedText,
					]
					clause.segmentation.segmentedParams = [
						...clause.segmentation.segmentedParams,
						...subClause.segmentation.segmentedParams,
					]
					clause.segmentation = idfySegmentation(clause.segmentation, genIdFactory(clause.index))
				} else {
					let prevSubClause = clause.subClauses[subClauseIdx - 1];
					prevSubClause.segmentation.segmentedText = [
						...prevSubClause.segmentation.segmentedText,
						["", `\n${subClause.name}\n`, SegmentedTextType.STATIC],
						...subClause.segmentation.segmentedText,
					]
					prevSubClause.segmentation.segmentedParams = [
						...prevSubClause.segmentation.segmentedParams,
						...subClause.segmentation.segmentedParams,
					]
					prevSubClause.segmentation = idfySegmentation(prevSubClause.segmentation, genIdFactory(prevSubClause.index))
					clause.subClauses[subClauseIdx - 1] = prevSubClause;
				}
			}
			clause.subClauses = clause.subClauses.filter(sc => sc.id !== subClauseId);
			newContractTemplateValue.clauses[clauseIdx] = clause;
		} else {
			const result = await contractTemplateClient.deleteClause(clauseId);
			if (result) {
				newContractTemplateValue.clauses = newContractTemplateValue.clauses.filter(c => c.id !== clauseId);
				toast.success(t(translationPath + "toasts.deleteClause.success"))
			}
		}
		setContractTemplate(newContractTemplateValue);
		return newContractTemplateValue

	}
	const updateClauseContent: EditContractTemplateContextType['updateClauseContent'] = async (clauseId: ClauseEntity['id'], newClause: ClauseEntity) => {
		const oldClause = contractTemplate.clauses.find(c => c.id === clauseId)
		const newSegmentation = idfySegmentation(newClause.segmentation, genIdFactory(oldClause.index))
		oldClause.segmentation = newSegmentation
		// update subclauses of the clause
		oldClause.subClauses = oldClause.subClauses.map((subClause, index) => {
			const newSubClause = newClause.subClauses[index] || subClause
			const newSegmentation = idfySegmentation(newSubClause.segmentation, genIdFactory(newSubClause.index))
			newSubClause.segmentation = newSegmentation
			return newSubClause
		})
		const newContractTemplateValue = { ...contractTemplate }
		const clauseIdx = newContractTemplateValue.clauses.findIndex(c => c.id === clauseId)
		newContractTemplateValue.clauses[clauseIdx] = oldClause
		setContractTemplate(newContractTemplateValue)
	}
	const onClauseSwap: EditContractTemplateContextType['onClauseSwap'] = async (index: number, localId: number, externalId: number, externalName: string): Promise<ContractTemplateEntity> => {
		const newContractTemplateValue = { ...contractTemplate }
		const result = await contractTemplateClient.deleteClause(localId);
		if (result) {
			newContractTemplateValue.clauses = newContractTemplateValue.clauses.filter(c => c.id !== localId);
			toast.success(t(translationPath + "toasts.deleteClause.success"))
		}
		const row = await insertExistingClauseInContractTemplate(externalId, externalName)
		if (!row.id || !row.code) {
			return contractTemplate
		}
		const newClause: ClauseEntity = {
			...row,
			index: index.toString(),
		}
		newClause.segmentation = idfySegmentation(newClause.segmentation, genIdFactory(newClause.index))
		// remove clause if duplicate
		newContractTemplateValue.clauses = newContractTemplateValue.clauses.filter(c => normalizeName(c.name) !== normalizeName(newClause.name))
		// reinsert new clause
		newContractTemplateValue.clauses.splice(index, 0, newClause)
		const newContractTemplate = fixTemplateIndexation(newContractTemplateValue)
		// save template
		setContractTemplate(newContractTemplate)
		return newContractTemplate
	}

	const onSegmentDelete: EditContractTemplateContextType['onSegmentDelete'] = (clauseId: ClauseEntity['id'], subClauseId: SubClauseEntity['id'], id: string, styledSegments: ClauseEntity['segmentation']['segmentedText'], deletedSegments: RenderSegments) => {
		let clause: ClauseEntity
		let subClause: SubClauseEntity
		let clauseIdx: number
		let subClauseIdx: number
		let segmentation: ClauseEntity['segmentation']
		clauseIdx = contractTemplate.clauses!.findIndex(c => c.id == clauseId)
		clause = contractTemplate.clauses[clauseIdx]

		if (subClauseId) {
			subClauseIdx = clause.subClauses!.findIndex(sc => sc.id == subClauseId)
			subClause = clause.subClauses[subClauseIdx]
			segmentation = subClause.segmentation
		} else {
			segmentation = clause.segmentation
		}
		segmentation = deleteSegment(segmentation.segmentedText, segmentation.segmentedParams, id, styledSegments, deletedSegments)

		if (subClauseId) {
			segmentation = idfySegmentation(segmentation, genIdFactory(subClause.index))
			subClause.segmentation = segmentation
			clause.subClauses = [...clause.subClauses]
		} else {
			segmentation = idfySegmentation(segmentation, genIdFactory(clause.index))
			clause.segmentation = segmentation
		}
		let newContractTemplateValue = { ...contractTemplate }
		newContractTemplateValue.clauses[clauseIdx] = clause
		setContractTemplate(newContractTemplateValue)
	}

	const insertClause: EditContractTemplateContextType['insertClause'] = async (insertIndex: number, clauseName: string): Promise<ContractTemplateEntity> => {
		const row = await insertClauseInContractTemplate(clauseName)
		if (!row.id || !row.code) return;
		const newClause: ClauseEntity = {
			...row,
			name: clauseName,
			index: insertIndex.toString(),
			subClauses: [],
		}
		const newContractTemplateValue = { ...contractTemplate }
		// reinsert new clause
		newContractTemplateValue.clauses.splice(insertIndex, 0, newClause)
		const newContractTemplate = fixTemplateIndexation(newContractTemplateValue)
		setContractTemplate(newContractTemplate)
		return newContractTemplate
	}

	const insertImportedClause: EditContractTemplateContextType['insertImportedClause'] = async (insertIndex: number, clauseId: ClauseEntity['id'], newName: string): Promise<ContractTemplateEntity> => {
		const row = await insertExistingClauseInContractTemplate(clauseId, newName)
		if (!row.id || !row.code) {
			return contractTemplate
		}
		const newClause: ClauseEntity = {
			...row,
			index: insertIndex.toString(),
		}
		newClause.segmentation = idfySegmentation(newClause.segmentation, genIdFactory(newClause.index))

		const newContractTemplateValue = { ...contractTemplate }
		// remove clause if duplicate
		newContractTemplateValue.clauses = newContractTemplateValue.clauses.filter(c => c.name !== newClause.name)
		// reinsert new clause
		newContractTemplateValue.clauses.splice(insertIndex, 0, newClause)
		const newContractTemplate = fixTemplateIndexation(newContractTemplateValue)
		// save template
		setContractTemplate(newContractTemplate)
		//EventManager.invoke('SaveContractTemplate', true)
		return contractTemplate
	}

	const onClauseNameChange: EditContractTemplateContextType['onClauseNameChange'] = (clauseId: ClauseEntity['id'], subClauseId: SubClauseEntity['id'], name: string) => {
		let clause: ClauseEntity
		let subClause: SubClauseEntity
		let clauseIdx: number
		let subClauseIdx: number
		let segmentation: ClauseEntity['segmentation']
		clauseIdx = contractTemplate.clauses!.findIndex(c => c.id == clauseId)
		clause = contractTemplate.clauses[clauseIdx]

		if (subClauseId) {
			subClauseIdx = clause.subClauses!.findIndex(sc => sc.id == subClauseId)
			subClause = clause.subClauses[subClauseIdx]
			segmentation = subClause.segmentation
		} else {
			segmentation = clause.segmentation
		}

		if (subClause) {
			subClause.name = name;
			clause.subClauses[subClauseIdx] = subClause;
		} else {
			clause.name = name;
		}
		let newContractTemplateValue = { ...contractTemplate }
		newContractTemplateValue.clauses[clauseIdx] = clause;
		setContractTemplate(newContractTemplateValue);
	}

	const insertSubClause: EditContractTemplateContextType['insertSubClause'] = async (insertIndex: number, clauseId: ClauseEntity['id'], subClauseName: string): Promise<ContractTemplateEntity> => {
		const result = await insertSubClauseInContractTemplate(clauseId, subClauseName)
		if (!result.id || !result.code) return;
		const newContractTemplateValue = { ...contractTemplate };
		const newSubClause: SubClauseEntity = {
			...result,
			name: subClauseName,
			index: insertIndex.toString(),
		}
		const clauseIdx = newContractTemplateValue.clauses!.findIndex(c => c.id === clauseId);
		if (clauseIdx == -1) return;
		const clause = { ...newContractTemplateValue.clauses[clauseIdx] };
		clause.subClauses.splice(insertIndex, 0, newSubClause);
		const newClause = fixClauseIndexation(clause)
		newContractTemplateValue.clauses[clauseIdx] = newClause;
		setContractTemplate(newContractTemplateValue);
		return newContractTemplateValue
	}

	const insertSubClauseWithContent: EditContractTemplateContextType['insertSubClauseWithContent'] = async (insertIndex: number, clauseId: ClauseEntity['id'], subClause: SubClauseEntity): Promise<ContractTemplateEntity> => {
		const result = await insertSubClauseInContractTemplate(clauseId, subClause.name)
		if (!result.id || !result.code) return;
		const newContractTemplateValue = { ...contractTemplate };
		const newSubClause: SubClauseEntity = {
			...result,
			name: subClause.name,
			index: insertIndex.toString(),
			segmentation: idfySegmentation(subClause.segmentation, genIdFactory(insertIndex.toString()))
		}
		const clauseIdx = newContractTemplateValue.clauses!.findIndex(c => c.id === clauseId);
		if (clauseIdx == -1) return;
		const clause = { ...newContractTemplateValue.clauses[clauseIdx] };
		clause.subClauses.splice(insertIndex, 0, newSubClause);
		const newClause = fixClauseIndexation(clause)
		newContractTemplateValue.clauses[clauseIdx] = newClause;
		setContractTemplate(newContractTemplateValue);
		return newContractTemplateValue
	}

	const contractListParentRef = useRef()
	const EditContractTemplateContextValue: EditContractTemplateContextType = useMemo(() => {
		return {
			contractTemplate,
			setContractTemplate,
			combinedTemplateParams,
			setCombinedTemplateParams,
			previewValues,
			setPreviewValues,
			onParamChanged,
			contractListParentRef,
			onParamDelete,
			onOptionAdd,
			onSegmentChange,
			onAddParam,
			onOptionChanged,
			onOptionDelete,
			onParamTypeChange,
			onParamReorder,
			onClauseDelete,
			onSegmentDelete,
			insertClause,
			onClauseNameChange,
			insertSubClause,
			onParamNameChanged,
			initialTemplate,
			setInitialTemplate,
			updateInitialTemplate,
			insertImportedClause,
			insertSubClauseWithContent,
			onClauseSwap,
			onSelectGroupInClause,
			groups,
			setTemplateGroups,
			onApplySegmentation,
			updateClauseContent
		}

	}, [contractTemplate, combinedTemplateParams, previewValues, contractListParentRef, initialTemplate, onParamNameChanged, setTemplateGroups])
	useEffect(() => {
		EventManager.invoke("EditContractTemplateContext", EditContractTemplateContextValue)
		return () => {
		}
	}, [EditContractTemplateContextValue])

	return (
		<EditContractTemplateContext.Provider value={EditContractTemplateContextValue}>
			<div className="contract-container d-flex justify-content-between align-items-center">
				{!contractTemplate ? (
					<div className="contract-loading-container">
						<Loading height="90vh" />
					</div>
				) : (
					<EditContractTemplate />
				)}
			</div>
		</EditContractTemplateContext.Provider>
	);
}

export default ContractTemplatePage;
